import Phaser from 'phaser';
import Platform from '../gameobjects/Platform';
import { getEventEmitter } from "../services/EventService";
import { getRandomPlatformsSetVariants } from '../services/PlatformService';
import { getActiveToken } from "../services/TokenService";

export class Game extends Phaser.Scene {

    /** @type {Phaser.Physics.Arcade.StaticGroup} */
    platforms;

    /** @type {Phaser.Physics.Arcade.Sprite} */
    player;

    /** @type {Phaser.GameObjects.Text} */
    scoreText;

    constructor() {
        super('game');
    }

    init() {

        // Constants
        this.platformHeight = 84;
        this.platformsSetsMaxCount = 18;

        // Variables
        this.playerStartPosition = { x: this.scale.width / 2, y: this.scale.height / 2 };
        this.playerScore = 0;
        this.playerToken = getActiveToken();
    }

    preload() {

        this.load.image('background', require('../assets/background.png'));
        this.load.image('background_score', require('../assets/ui/background_score.png'));
        this.load.image('background_prisoner_name', require('../assets/ui/background_prisoner_name.png'));
        this.load.image('button_left', require('../assets/ui/button_left.png'));
        this.load.image('button_right', require('../assets/ui/button_right.png'));
        this.load.image('platform_standard', require('../assets/platform_standard.png'));
        this.load.image('platform_blank', require('../assets/platform_blank.png'));
        this.load.image('platform_breaking', require('../assets/platform_breaking.png'));
        this.load.image('platform_dynamic', require('../assets/platform_dynamic.png'));
        this.load.image('platform_saw', require('../assets/platform_saw.png'));
        this.load.image('player_fall', require(`../assets/players/${this.playerToken.edition}/fall.png`));
        this.load.image('player_jump', require(`../assets/players/${this.playerToken.edition}/jump.png`));

        this.cursors = this.input.keyboard.createCursorKeys();
    }

    create() {

        // Create background
        this.add.image(this.scale.width / 2, this.scale.height / 2, 'background')
            .setScale(1.2)
            .setScrollFactor(1, 0);

        // Create text with score
        this.add.image(this.scale.width / 2, 100, 'background_score')
            .setScale(0.3)
            .setScrollFactor(1, 0)
            .setDepth(2)
            .setAlpha(0.8);
        this.scoreText = this.add.text(this.scale.width / 2, 85, '0', { color: '#FFF', fontSize: 32 })
            .setScrollFactor(0)
            .setOrigin(0.5, 0)
            .setDepth(2);

        // Create text with prisoner name
        this.add.image(this.scale.width / 2, 30, 'background_prisoner_name')
            .setScale(0.3)
            .setScrollFactor(1, 0)
            .setDepth(2)
            .setAlpha(0.8);
        this.add.text(this.scale.width / 2, 20, this.playerToken.name, { color: '#FFF', fontSize: 24 })
            .setScrollFactor(0)
            .setOrigin(0.5, 0)
            .setDepth(2);

        // Create move buttons
        this.add.image(this.scale.width / 2 - 180, this.scale.height - 80, 'button_left')
            .setScale(0.3)
            .setScrollFactor(1, 0)
            .setDepth(2)
            .setInteractive({ cursor: 'pointer' })
            .on('pointerdown', function (pointer) {
                this.scene.movePlayer("left");
                this.alpha = 0.5;
            })
            .on('pointerup', function (pointer) {
                this.scene.movePlayer("none");
                this.alpha = 1.0;
            });
        this.add.image(this.scale.width / 2 + 180, this.scale.height - 80, 'button_right')
            .setScale(0.3)
            .setScrollFactor(1, 0)
            .setDepth(2)
            .setInteractive({ cursor: 'pointer' })
            .on('pointerdown', function (pointer) {
                this.scene.movePlayer("right");
                this.alpha = 0.5;
            })
            .on('pointerup', function (pointer) {
                this.scene.movePlayer("none");
                this.alpha = 1.0;
            });

        // Create player
        this.player = this.physics.add.sprite(this.playerStartPosition.x, this.playerStartPosition.y, 'player_fall')
            .setSize(230, 400)
            .setScale(0.3)
            .setDepth(1);

        // Create platforms
        this.platforms = this.physics.add.staticGroup({ classType: Platform, runChildUpdate: true });

        // Add first platforms
        for (let i = 0; i < this.platformsSetsMaxCount; i++) {
            this.addNewPlatformsSet();
        }

        // Create camera
        this.cameras.main.startFollow(this.player);
        this.cameras.main.setDeadzone(this.scale.width * 1.5);

        // Emit game over event if down key "O"
        this.input.keyboard.once('keydown-O', () => {
            getEventEmitter().emit("gameOver");
        })

        // Listen game over event 
        getEventEmitter().on("gameOver", this.handleGameOver, this);
    }

    update() {

        this.removeLastPassedPlatform();

        this.addNewPlatformsSet();

        // Set stand texture to player
        const vy = this.player.body.velocity.y
        if (vy > 0 && this.player.texture.key !== 'player_fall') {
            this.player.setTexture('player_fall')
        }

        // Left and right input logic
        if (this.cursors.left.isDown) {
            this.movePlayer("left");
        }
        else if (this.cursors.right.isDown) {
            this.movePlayer("right");
        }
        else if (!this.input.activePointer.isDown) {
            this.player.setVelocityX(0)
        }

        // Wrap player if moved off screen
        this.wrapPlayer(this.player);

        // Emit game over event if player under bottom platform
        const firstPlatform = this.platforms.getChildren().at(0);
        if (this.player.y > firstPlatform.y + 500) {
            getEventEmitter().emit("gameOver");
        }

        // Update score text
        const currentScore = -1 * (this.player.y - this.playerStartPosition.y);
        if (currentScore > this.playerScore) {
            this.playerScore = Phaser.Math.CeilTo(currentScore, 0, 10);
            this.scoreText.text = `${this.playerScore}`;
        }
    }

    /**
    * @param {Phaser.GameObjects.Sprite} player
    */
    wrapPlayer(player) {
        const halfWidth = player.displayWidth * 0.5
        const gameWidth = this.scale.width
        if (player.x < -halfWidth) {
            player.x = gameWidth + halfWidth
        }
        else if (player.x > gameWidth + halfWidth) {
            player.x = -halfWidth
        }
    }

    /**
     * @param {("none"|"left"|"right")} direction 
     */
    movePlayer(direction) {
        if (direction === "none") {
            this.player.setVelocityX(0);
        }
        if (direction === "left" && !this.player.body.onFloor()) {
            this.player.setVelocityX(-600);
            this.player.flipX = true;
        }
        if (direction === "right" && !this.player.body.onFloor()) {
            this.player.setVelocityX(600);
            this.player.flipX = false;
        }
    }

    addNewPlatformsSet() {

        if (this.platforms.children.size < this.platformsSetsMaxCount) {

            // Get variants for new platforms set
            const newPlatformsSetVariants = getRandomPlatformsSetVariants(this.playerScore);

            // Convert variants to platform game objects
            let newPlatformsSet = [];
            let lastPlatform = this.platforms.getChildren().at(-1);
            newPlatformsSetVariants.forEach(newPlatformVariant => {

                const x = Phaser.Math.Between(100, this.scale.width - 100);
                const y = lastPlatform ? lastPlatform.y - this.platformHeight : this.scale.height;

                const platform = new Platform(this, x, y, newPlatformVariant, this.player);

                newPlatformsSet.push(platform);

                lastPlatform = platform;
            });

            // Add platforms to group
            newPlatformsSet.forEach(platform => this.platforms.add(platform, true));
        }
    }

    removeLastPassedPlatform() {

        let lastPassedPlatform;

        this.platforms.children.iterate(child => {
            if (child.y >= this.cameras.main.scrollY + 1300) {
                lastPassedPlatform = child;
            }
        });

        if (lastPassedPlatform != null) {
            this.platforms.remove(lastPassedPlatform, true, true);
        }
    }

    handleGameOver() {
        this.scene.start('game-over', { playerScore: this.playerScore })
    }
}